/**
 * Storefront product-video upload profile + the browser-side probe that
 * enforces it.
 *
 * We deliberately do NOT transcode anywhere in this pipeline — there is no
 * ffmpeg on the box and no MediaConvert bill. Instead the clip is measured
 * here, in the uploader's browser, and rejected before a single byte crosses
 * the wire if it misses the profile. That keeps bad media out of S3 and out
 * of the storefront, at zero server cost.
 *
 * These constants mirror `item-videos.service.ts` on the backend, which
 * re-validates everything (a browser can be bypassed). Change both together.
 */
export const VIDEO_ACCEPTED = ['video/mp4'];
export const VIDEO_MAX_BYTES = 100 * 1024 * 1024;
export const VIDEO_MAX_DURATION_SEC = 60;
export const VIDEO_MAX_WIDTH = 1920;
export const VIDEO_MAX_HEIGHT = 1080;

export interface VideoProbe {
  durationSec: number;
  width: number;
  height: number;
}

/** Read duration + intrinsic resolution from a local file, without uploading. */
export function probeVideo(file: File): Promise<VideoProbe> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.muted = true;
    // Some browsers refuse to decode metadata for an off-DOM element unless
    // playback is explicitly inline-permitted.
    el.playsInline = true;

    const cleanup = () => {
      el.removeAttribute('src');
      el.load();
      URL.revokeObjectURL(url);
    };

    el.onloadedmetadata = () => {
      const probe = {
        durationSec: el.duration,
        width: el.videoWidth,
        height: el.videoHeight,
      };
      if (!Number.isFinite(probe.durationSec) || probe.width === 0) {
        cleanup();
        reject(new Error('This file could not be read as a video.'));
        return;
      }
      cleanup();
      resolve(probe);
    };
    el.onerror = () => {
      cleanup();
      reject(
        new Error(
          'This file could not be read as a video. Export it as MP4 (H.264) and try again.',
        ),
      );
    };
    el.src = url;
  });
}

/**
 * Grab a still from the clip to use as its poster.
 *
 * Seeks a short way in (never past the midpoint of a very short clip) because
 * frame zero of a product video is often a fade-in or a blank frame. The
 * result is what a shopper sees before pressing play, so it has to be a real
 * frame — this is the asset that lets the portal set `preload="none"` and pay
 * no video egress on page load.
 */
export function capturePoster(file: File, atSec = 1): Promise<Blob | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement('video');
    el.preload = 'auto';
    el.muted = true;
    el.playsInline = true;
    let settled = false;

    const finish = (blob: Blob | null) => {
      if (settled) return;
      settled = true;
      el.removeAttribute('src');
      el.load();
      URL.revokeObjectURL(url);
      resolve(blob);
    };

    // A poster is a nice-to-have, never a blocker: if anything here fails the
    // upload still proceeds without one.
    el.onerror = () => finish(null);
    const bail = setTimeout(() => finish(null), 10_000);

    el.onloadedmetadata = () => {
      const target = Math.min(atSec, (el.duration || 2) / 2);
      el.currentTime = Number.isFinite(target) && target > 0 ? target : 0;
    };

    el.onseeked = () => {
      clearTimeout(bail);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = el.videoWidth;
        canvas.height = el.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return finish(null);
        ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((b) => finish(b), 'image/jpeg', 0.82);
      } catch {
        // Tainted canvas or an unusual codec — proceed posterless.
        finish(null);
      }
    };

    el.src = url;
  });
}

/** Human-readable clip length, e.g. `0:08`. */
export function formatDuration(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return '—';
  const s = Math.round(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Validate a picked file against the profile. Returns an actionable message
 * naming what is wrong AND how to fix it, or null when the file is good.
 */
export function validateVideo(file: File, probe: VideoProbe): string | null {
  if (!VIDEO_ACCEPTED.includes(file.type)) {
    return `${file.type || 'That file type'} isn't supported — export the clip as MP4 (H.264) and try again.`;
  }
  if (file.size > VIDEO_MAX_BYTES) {
    return `This clip is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is ${
      VIDEO_MAX_BYTES / 1024 / 1024
    } MB. Export it at 1080p and try again.`;
  }
  if (probe.durationSec > VIDEO_MAX_DURATION_SEC + 0.5) {
    return `This clip is ${Math.round(
      probe.durationSec,
    )} seconds — the limit is ${VIDEO_MAX_DURATION_SEC}. Trim it and try again.`;
  }
  const longEdge = Math.max(probe.width, probe.height);
  const shortEdge = Math.min(probe.width, probe.height);
  if (longEdge > VIDEO_MAX_WIDTH || shortEdge > VIDEO_MAX_HEIGHT) {
    return `This clip is ${probe.width}×${probe.height} — the limit is ${VIDEO_MAX_WIDTH}×${VIDEO_MAX_HEIGHT} (1080p). Export it smaller and try again.`;
  }
  return null;
}
